import type { Position, Size } from './canvasMath';

export interface MaskTransform {
  position: Position;
  scale: number;
}

export interface Stroke {
  points: number[];
  brushSize: number;
}

const FLOAT_EPSILON = 1e-9;

/**
 * Convert a stage-space point (where Konva pointer events live) into the
 * image's local coordinate system. The image is positioned at
 * `transform.position` and uniformly scaled by `transform.scale`, so the
 * inverse is `(screen − position) / scale`.
 *
 * Returns the screen point unchanged when `scale` is non-positive — defensive
 * against transient mid-mount transforms that would otherwise produce NaN /
 * Infinity coordinates.
 */
export function screenToImagePoint(screen: Position, transform: MaskTransform): Position {
  if (!Number.isFinite(transform.scale) || transform.scale <= FLOAT_EPSILON) {
    return { x: screen.x, y: screen.y };
  }
  return {
    x: (screen.x - transform.position.x) / transform.scale,
    y: (screen.y - transform.position.y) / transform.scale,
  };
}

/**
 * Convert a screen-space brush size (the UI slider unit) into image-space px.
 * Mask strokes are stored in image space so they remain aligned when the user
 * pans / zooms, and so the data-URL we ship to the backend matches the source
 * image's pixel dimensions exactly.
 */
export function screenToImageBrushSize(
  screenBrushSize: number,
  transform: MaskTransform,
): number {
  if (!Number.isFinite(transform.scale) || transform.scale <= FLOAT_EPSILON) {
    return screenBrushSize;
  }
  return screenBrushSize / transform.scale;
}

export function strokesAreEmpty(strokes: Stroke[]): boolean {
  return !strokes.some(s => s.points.length >= 2);
}

export interface StrokeDrawContext {
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  lineCap: CanvasLineCap;
  lineJoin: CanvasLineJoin;
  globalCompositeOperation: GlobalCompositeOperation;
  clearRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  arc(x: number, y: number, radius: number, start: number, end: number): void;
  fill(): void;
}

/**
 * Paint `strokes` onto a 2D drawing context that's been sized to the source
 * image's pixel dimensions. Single-point strokes (a single click without
 * drag) render as a filled circle so the user sees feedback for taps. The
 * resulting alpha mask uses opaque white on transparent — the convention
 * fal.ai inpainting endpoints expect once the BE wraps it as a data URL.
 *
 * Pure with respect to inputs: caller owns the context lifecycle. Extracted
 * from `renderStrokesToDataUrl` so the draw-call sequence is testable without
 * a real CanvasRenderingContext2D (jsdom does not ship one).
 */
export function applyStrokesToContext(
  strokes: Stroke[],
  ctx: StrokeDrawContext,
  imageSize: Size,
): void {
  ctx.clearRect(0, 0, imageSize.width, imageSize.height);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = 'rgba(255, 255, 255, 1)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const stroke of strokes) {
    const { points, brushSize } = stroke;
    if (points.length < 2) continue;

    if (points.length === 2) {
      ctx.beginPath();
      ctx.arc(points[0], points[1], brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) {
      ctx.lineTo(points[i], points[i + 1]);
    }
    ctx.stroke();
  }
}

/**
 * Render `strokes` to a data URL sized to `imageSize` (the source image's
 * pixel dimensions, not the stage's). Returns `null` when the host lacks a
 * 2D context — happens in jsdom-driven tests and is the caller's signal to
 * skip the `onMaskChange` callback. The component layer is responsible for
 * deciding whether to surface a "no mask" event in that branch.
 */
export function renderStrokesToDataUrl(strokes: Stroke[], imageSize: Size): string | null {
  if (imageSize.width <= 0 || imageSize.height <= 0) return null;
  if (strokesAreEmpty(strokes)) return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(imageSize.width);
  canvas.height = Math.round(imageSize.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  applyStrokesToContext(strokes, ctx as unknown as StrokeDrawContext, imageSize);
  return canvas.toDataURL('image/png');
}

/**
 * Strip the `data:image/...;base64,` prefix from a data URL, returning the
 * raw base64 payload that the SSGP v3 `Inpaint` / `RemoveObject` request DTOs
 * accept on `MaskBase64`. The backend's `ResolveMaskAsDataUrl` helper
 * re-prefixes when forwarding to fal.ai, so we ship the bare payload.
 */
export function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}
